import { IEnquesta } from 'bg-server/src/components/api/v1/enquesta/types';

import { IUser } from 'src/components';

export function getPercentString(percentage: number, total: number, digits = 0) {
    return ((percentage / total) * 100).toFixed(digits);
}

function getCryptoRandomInt(min, max) {
    const range = max - min + 1;
    const buffer = new Uint32Array(1);
    window.crypto.getRandomValues(buffer);
    const randomNumber = buffer[0] % range;
    return min + randomNumber;
}

export function getDefaultId() {
    const timestamp = new Date().getTime().toString();
    const randomNumbers = getCryptoRandomInt(0, 10000);
    return `default-${timestamp}-${randomNumbers}`;
}

export function getRandomString(length = 6) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
        result += characters.charAt(Math.floor(getCryptoRandomInt(0, charactersLength - 1)));
        counter += 1;
    }
    return result;
}

/**
 * Remove all spaces and first character from a string
 *
 * Example: `+34 123 456 789 ` will be `34123456789`
 */
export function clearPhoneNumber(phoneNumber: string) {
    if (!phoneNumber || phoneNumber === '') {
        return '';
    }
    return phoneNumber.split(' ').join('').substring(1);
}

export const formatNumber = (number: string) => `+${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5, 8)} ${number.substring(8, 11)}`;

/**
 * Convierte un enum a un objeto `{ clave: valor }`. Por ejemplo:
 * ```ts
 * enum EWeekdays {
 *     Monday = 2, Tuesday, Wednesday
 * }
 * ```
 * Quedará convertido en:
 * ```json
 * {
 *     "Monday": 2,
 *     "Tuesday": 3,
 *     "Wednesday": 4
 * }
 * ```
 * @param enumSource enum
 * @returns objeto
 */
export function enum2Object(enumSource: any): { [key: string]: number } {
    return Object.values(enumSource).reduce<{ [key: string]: number }>((obj, rt: string) => (
        (typeof rt === 'string') ? ({ ...obj, [rt]: enumSource[rt] }) : obj
    ), {});
}

/**
 * Convierte un enum a un objeto array con los nombres de los elementos del enum
 * @param enumSource enum
 * @returns array
 */
export function enum2Arr(enumSource: any): string[] {
    return Object.values(enumSource).filter(obj => typeof obj === 'string') as string[];
}

/**
 * **OJO!! esta función cambia el objeto original.**
 *
 * Actualiza un objeto JSON accediendo a los diferentes niveles a través de la ruta especificada.
 * Por ejemplo, para cambiar el valor del nivel3:
 * ```json
 * {
 *      "nivel1": {
 *          "nivel 2": [
 *              {},
 *              { "nivel3": "valor" },
 *              ...
*           ]
 *      }
 * }
 * ```
 * Se haría con un path: `nivel1."nivel 2".1.nivel3`
 * @param obj objeto JSON a modificar.
 * @param path ruta de acceso en formato 'nivel1.nivel2.2.nivel3'
 * @param value nuevo valor para la ruta especificada en path
 */
export function updateJson(obj: any, path: string, value: any): void {
    const pathParts = path.split('.');
    let currentPart = obj;

    for (let i = 0; i < pathParts.length - 1; i += 1) {
        const key = pathParts[i].replace(/"/ig, '');
        const isNumericKey = !Number.isNaN(Number(key));
        const isLastPath = i === pathParts.length - 1;
        const nextKey = isLastPath ? '' : pathParts[i + 1].replace(/"/ig, '');
        const isNextKeyNumeric = !Number.isNaN(Number(nextKey));
        const isKeyNotExists = currentPart[key] === null || currentPart[key] === undefined;

        if (isKeyNotExists) {
            currentPart[key] = isNextKeyNumeric ? [] : {};
        } else if (!isLastPath && typeof currentPart[key] !== 'object') {
            currentPart[key] = isNumericKey ? [] : {};
        }

        currentPart = currentPart[key];
    }

    currentPart[pathParts[pathParts.length - 1]] = value;
}

/**
 * Devuelve un objeto JSON modificando el valor accediendo a través de la ruta especificada.
 * Por ejemplo, para cambiar el valor del nivel3:
 * ```json
 * {
 *      "nivel1": {
 *          "nivel 2": [
 *              {},
 *              { "nivel3": "valor" },
 *              ...
*           ]
 *      }
 * }
 * ```
 * Se haría con un path: `nivel1."nivel 2".1.nivel3`
 * Si la ruta no existe, la ruta se crea.
 * @param obj objeto JSON a modificar.
 * @param path ruta de acceso en formato 'nivel1.nivel2.2.nivel3'
 * @param value nuevo valor para la ruta especificada en path
 */
export function updateJsonInmutable<T extends object>(obj: T, path: string, value: any): T {
    const newObj = JSON.parse(JSON.stringify(obj));
    updateJson(newObj, path, value);
    return newObj;
}

export function getDataFromSurveys(surveys: IEnquesta[]) {
    const data = surveys.reduce((acc, survey) => {
        if (!acc[survey.formType]) {
            acc[survey.formType] = {};
        }

        Object.keys(survey.preguntes).forEach((pKey) => {
            const value = survey.preguntes[pKey];
            if (value !== null && value !== undefined) {
                // eslint-disable-next-line no-param-reassign
                acc[survey.formType][pKey] = value;
            }
        });
        return acc;
    }, {});
    return data;
}

// eslint-disable-next-line no-new-func, @typescript-eslint/no-implied-eval
const evil = (fn: any) => new Function('data', `return ${fn}`);

export const evalit = (fn: any, data: any, def = false): any => {
    let result = false;

    try {
        result = fn(data);
    } catch (err) {
        result = def;
    }
    return result;
};

export const getEvils = (fields: any[]): any => fields.reduce((curr: any, field: any) => {
    if (field.showIf && field.showIf !== '') {
        // Regex para proteger de inyección de código
        const regex = /(data(?:\['\w+'\])+)\s*(==|!=|<|>|<=|>=)?\s*(?:'([^']*)'|"([^"]*)"|(\w+))?/;
        if (regex.test(field.showIf)) {
            // eslint-disable-next-line no-param-reassign
            curr[field.title] = evil(field.showIf);
        }
    }
    return curr;
}, {});

export const toTitleCase = (str: string) => str.replace(
    /\w\S*/g,
    txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
);

export const date2YMD = (dateStr: Date) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    // Sumamos 1 al mes y aseguramos que siempre tenga dos dígitos
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    // Aseguramos que el día del mes siempre tenga dos dígitos
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const directionKeyMap = {
    tv: 'street_type',
    nv: 'street_name',
    nu: 'street_number',
    es: 'escala',
    pi: 'pis',
    po: 'porta',
    cp: 'codi_postal',
    di: 'districte',
    ba: 'nom_barri',
};

/**
 * Formatea una dirección según el formato especificado.
 * Los atajos disponibles son:
 * - {tv} para el tipo de vía
 * - {nv} para el nombre de la vía
 * - {nu} para el número
 * - {es} para la escalera
 * - {pi} para el piso
 * - {po} para la puerta
 * - {cp} para el código postal
 * - {di} para el distrito
 * - {ba} para el barrio
 * @param direccion dirección a formatear
 * @param formato formato de salida
 * @returns dirección formateada
 */
export const formatDireccion = (direccion: any, formato: string): string => {
    // Usamos una expresión regular para encontrar las claves en el formato
    const regex = /\b(tv|nv|nu|es|pi|po|cp|di|ba)\b/g;

    return formato.replace(regex, (match) => {
        const claveReal = directionKeyMap[match as keyof typeof directionKeyMap];
        return direccion[claveReal] ?? '';
    });
};

export const getQueryParam = (url: string, param: string): string => {
    const hashParts = url.split('#');
    let queryParams = '';
    if (hashParts.length > 1) {
        const queryStart = hashParts[1].indexOf('?');
        if (queryStart !== -1) {
            queryParams = hashParts[1].substring(queryStart + 1);
        }
    }
    const params = new URLSearchParams(queryParams);
    return params.get(param);
};

export const diasDesdeFecha = (isoString: string) => {
    const fecha = new Date(isoString);
    const hoy = new Date();
    fecha.setHours(0, 0, 0, 0);
    hoy.setHours(0, 0, 0, 0);
    const diferencia = hoy.getTime() - fecha.getTime();
    const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24));
    return dias > 0 ? dias : 0;
};

export const arrayHasFnName = (array: any[], name: string) => {
    if (!array || !array.length) return false;
    return array.some(fn => fn.name === name);
};

/** Elimina los valores nullos de un objeto */
export const removeNullValues = (obj: any) => {
    if (Array.isArray(obj)) {
        return obj.map(item => removeNullValues(item));
    } if (typeof obj === 'object' && obj !== null) {
        return Object.keys(obj)
            .filter(key => obj[key] !== null && obj[key] !== undefined)
            .reduce((acc, key) => {
                acc[key] = removeNullValues(obj[key]);
                return acc;
            }, {});
    }
    return obj;
};

/** Elimina las cadenas vacías de un objeto */
export const removeEmptyStrings = (obj: any) => {
    if (Array.isArray(obj)) {
        return obj.map(item => removeEmptyStrings(item));
    }
    if (typeof obj === 'object' && obj !== null) {
        return Object.keys(obj)
            .filter(key => obj[key] !== '') // Filtra las claves cuyo valor no sea una cadena vacía
            .reduce((acc, key) => {
                acc[key] = removeEmptyStrings(obj[key]);
                return acc;
            }, {});
    }
    return obj;
};

export function hasRole(user: IUser, role: RegExp) {
    return user.roles.some((r) => {
        // Needed so regex reinits
        role.lastIndex = 0; // eslint-disable-line no-param-reassign
        return role.test(r);
    });
}
/** Determina si el role o alguno de los roles de roleToInclude esta en rolesToSearchIn */
export function includeRole(roleToInclude: string | string[], rolesToSearchIn: string[]): boolean {
    // Verificar si rolesToSearchIn es undefined o tiene longitud 0
    if (!rolesToSearchIn || rolesToSearchIn.length === 0) {
        return false;
    }

    // Convertir roleToInclude a un array si no lo es
    const roles = Array.isArray(roleToInclude) ? roleToInclude : [roleToInclude];

    // Verificar si alguno de los roles está en rolesToSearchIn
    return roles.some(role => rolesToSearchIn.includes(role));
}

export const getValueByPath = (obj: any, path: string) => path.split('.').reduce((acc, part) => acc && acc[part], obj);

export function normalizeString(str: string) {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * Transforma el input del usuario en un string de regex validor para la buscaqueda de mongo
 * @param searchTerm string de búsqueda
 * @returns string de regex
 */
export function buildSearchRegex(searchTerm: string): string {
    const replacements: { [key: string]: string } = {
        a: '[aáàäâ]',
        e: '[eéèëê]',
        i: '[iíìïî]',
        o: '[oóòöô]',
        u: '[uúùüû]',
        n: '[nñ]',
    };

    // Escapar caracteres especiales de regex excepto `+`
    let escapedString = searchTerm.trim().replace(/[.*?^${}()|[\]\\]/g, '\\$&');

    // Reemplazar `+` por `.` (representa cualquier carácter único en regex)
    escapedString = escapedString.replace(/\+/g, '.');

    // Reemplazar uno o más espacios por \s+ para admitir múltiples espacios entre palabras
    escapedString = escapedString.replace(/\s+/g, '\\s+');

    // Reemplazar letras con acentos sin afectar otros caracteres
    const regexString = escapedString.toLowerCase().replace(/[aeioun]/g, match => replacements[match] || match);

    return regexString;
}
